import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { AcademicYearState } from '../academic-year/academic-year.state';
import { DotNetDatePipe } from '../pipes/dot-net-date.pipe';
import { IApplicationContent } from '../types/content.type';
import { INearbySchool } from '../types/nearby-school.type';
import { NearbySchoolsActions } from './nearby-schools.actions';
import { NearbySchoolsState } from './nearby-schools.state';
import { GuardianState } from '../guardian/guardian.state';
import { ChildState } from '../child/child.state';
import { combineLatest } from 'rxjs';
import { RootState } from '../store/root.state';
import { Household } from '../types';
import {} from 'googlemaps';
import { PlaceholderPipe } from '../pipes/placeholder.pipe';
import { NearbySchoolsListComponent } from './nearby-schools-list/nearby-schools-list.component';
import { NearbySchoolsMapComponent } from './nearby-schools-map/nearby-schools-map.component';
import { NgIf, NgFor } from '@angular/common';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { IAppSettings, APP_CONFIG } from '@nha/config/interfaces/iapp-settings.interface';

@Component({
    selector: 'app-nearby-schools',
    templateUrl: './nearby-schools.component.html',
    styleUrls: ['./nearby-schools.component.scss'],
    standalone: true,
    imports: [FormsModule, NgxsFormDirective, ReactiveFormsModule, NgIf, NearbySchoolsMapComponent, NearbySchoolsListComponent, NgFor, PlaceholderPipe]
})
export class NearbySchoolsComponent implements OnInit {
	appSettings = inject<IAppSettings>(APP_CONFIG);
	appContent: IApplicationContent;

	culture: string = window['nha-sa-culture'] || 'en-US';
	showNearbySchools = false;
	nearbySchoolsForm: UntypedFormGroup;
	today: Date = new Date();
	kenticoUrl: string = this.appSettings.web.schoolSites;
	academicYearApplying: number;
	schoolsToShow = 2;
	loading = true;
	nearbySchoolsExist;
	selectedSchoolID: number;
	guardianHouseholdLatLng: { lat: number; lng: number };
	currentGuardianHousehold: Household;
	private currentNearbySchoolsIDs: number[];

	constructor(
		private fb: UntypedFormBuilder,
		private dotNetDatePipe: DotNetDatePipe,
		private store: Store
	) {}

	ngOnInit(): void {
		this.appContent = this.store.selectSnapshot(RootState.content);

		this.createForm();

		const academicYear$ = this.store.select(AcademicYearState.selectedYear);
		const guardians$ = this.store.select(GuardianState.guardians);
		const children$ = this.store.select(ChildState.children);
		const nearbySchools$ = this.store.select(NearbySchoolsState.nearbySchools);

		guardians$.subscribe((guardians) => {
			if (guardians?.length > 0) {
				const guardianHouseholdLatLng = { lat: guardians[0]?.household?.lat, lng: guardians[0]?.household?.lng };
				if (
					guardianHouseholdLatLng.lat &&
					guardianHouseholdLatLng.lng &&
					this.guardianHouseholdLatLng?.lat !== guardianHouseholdLatLng.lat &&
					this.guardianHouseholdLatLng?.lng !== guardianHouseholdLatLng.lng
				) {
					this.guardianHouseholdLatLng = guardianHouseholdLatLng;
					this.store.dispatch(new NearbySchoolsActions.GetNearbySchools(this.guardianHouseholdLatLng.lat, this.guardianHouseholdLatLng.lng));
				} else if (
					guardians[0].household?.address &&
					guardians[0].household?.city &&
					guardians[0].household?.stateCode &&
					guardians[0].household?.zipCode &&
					guardians[0].household.address !== this.currentGuardianHousehold?.address &&
					guardians[0].household.city !== this.currentGuardianHousehold?.city &&
					guardians[0].household.stateCode !== this.currentGuardianHousehold?.stateCode &&
					guardians[0].household.zipCode !== this.currentGuardianHousehold?.zipCode
				) {
					this.currentGuardianHousehold = guardians[0].household;
					const geocoder = new google.maps.Geocoder();

					const geocoderRequest = <google.maps.GeocoderRequest>{
						address: `${guardians[0].household.address} ${guardians[0].household.city} ${guardians[0].household.stateCode}, ${guardians[0].household.zipCode}`
					};
					geocoder.geocode(geocoderRequest, (results) => {
						if (
							results &&
							results[0]?.geometry?.location?.lat() &&
							this.guardianHouseholdLatLng?.lat !== results[0]?.geometry?.location?.lat() &&
							results[0]?.geometry?.location?.lng() &&
							this.guardianHouseholdLatLng?.lng !== results[0]?.geometry?.location?.lng()
						) {
							this.guardianHouseholdLatLng = {
								lat: results[0].geometry.location.lat(),
								lng: results[0].geometry.location.lng()
							};
							this.store.dispatch(new NearbySchoolsActions.GetNearbySchools(this.guardianHouseholdLatLng.lat, this.guardianHouseholdLatLng.lng));
						}
					});
				}
			}
		});

		combineLatest(nearbySchools$, children$, academicYear$).subscribe(([nearbySchools, children, academicYear]) => {
			if (nearbySchools) {
				const childGradeLevelIds = [...new Set(children.map((c) => c.gradeLevelId))];
				const schoolsWithGrades = nearbySchools
					.filter((school) =>
						childGradeLevelIds.every((cgl) => {
							if (school.schoolID === 497) {
								// Westfield Charter Academy
								return [1, 2, 3, 4, 5, 6, 7].includes(cgl);
							} else if (school.schoolID === 508) {
								// Westfield Prep
								return [8, 9, 404, 405, 406].includes(cgl);
							} else if (school.content.currentAcademicYear === academicYear) {
								return school.content.currentGradeLevels.some((gl) => cgl === gl.gradeLevelID);
							} else if (school.content.nextAcademicYear === academicYear) {
								return school.content.nextGradeLevels.some((gl) => cgl === gl.gradeLevelID);
							} else {
								return false;
							}
						})
					)
					.filter(
						(s) =>
							academicYear === s.content.currentAcademicYear || new Date() > this.dotNetDatePipe.transform(s.content.nextOpenEnrollmentBeginDate)
					);

				const newNearbySchoolsIDs = schoolsWithGrades.map((s) => s.schoolID);

				if (
					this.currentNearbySchoolsIDs &&
					this.currentNearbySchoolsIDs.length > 0 &&
					newNearbySchoolsIDs.filter((x) => !this.currentNearbySchoolsIDs.includes(x)).length === 0 &&
					this.currentNearbySchoolsIDs.filter((x) => !newNearbySchoolsIDs.includes(x)).length === 0
				) {
					this.selectedSchoolID = null;

					return; // nearby schools list is the same
				}

				if (schoolsWithGrades && schoolsWithGrades.length > 0) {
					this.nearbySchoolsExist = true;
					this.currentNearbySchoolsIDs = newNearbySchoolsIDs;
					this.setForm(schoolsWithGrades);
				} else {
					this.selectedSchoolID = null;
					this.nearbySchoolsExist = false;
					this.currentNearbySchoolsIDs = [];
				}
			}
		});

		this.store.select(AcademicYearState.selectedYear).subscribe((year) => {
			if (year) {
				this.academicYearApplying = year;
				this.resetGradeLevelControls();
				this.validateGradeLevelControls();
			}
		});
	}

	createForm(): void {
		this.nearbySchoolsForm = this.fb.group({
			nearbySchools: this.fb.array([])
		});
	}

	setForm(data: INearbySchool[]): void {
		const schoolsFGs = data.map((nearbySchool) => this.fb.group(nearbySchool));
		schoolsFGs.forEach((fg) => {
			fg.addControl('isApplying', new UntypedFormControl(false));
			fg.addControl('gradeLevelId', new UntypedFormControl(''));
		});
		const schoolsFormArray = this.fb.array(schoolsFGs);
		this.nearbySchoolsForm.setControl('nearbySchools', schoolsFormArray);
		this.loading = false;
	}

	get nearbySchools(): UntypedFormArray {
		return this.nearbySchoolsForm.get('nearbySchools') as UntypedFormArray;
	}

	get addedNearbySchools() {
		return this.nearbySchools.controls.filter((school) => school.get('isApplying').value);
	}

	toggleNearbySchools(showMore: boolean): void {
		if (showMore) {
			this.schoolsToShow += 4;
		} else {
			this.schoolsToShow -= 4;
		}
	}

	removeSchoolToApply(schoolID: number): void {
		const schoolToRemove = this.nearbySchools.controls.find((c) => c.get('schoolID').value === schoolID);

		(schoolToRemove as UntypedFormGroup).controls.isApplying.setValue(false);
	}

	addSchoolToApply(schoolID: number) {
		(this.nearbySchools.controls.find((ns) => ns.get('schoolID').value === schoolID) as UntypedFormGroup).controls.isApplying.setValue(true);
	}

	resetGradeLevelControls(): void {
		if (this.nearbySchools && this.nearbySchools.controls.length) {
			this.nearbySchools.controls.forEach((control) => {
				control.get('gradeLevelId').setValue('');
			});
		}
	}

	validateGradeLevelControls(): void {
		if (this.nearbySchools && this.nearbySchools.controls.length) {
			this.nearbySchools.controls.forEach((control) => {
				const formGroup = control as UntypedFormGroup;
				const gradeLevel = formGroup.get('gradeLevelId');
				const isApplying = formGroup.get('isApplying');

				if (gradeLevel.value !== '') {
					gradeLevel.updateValueAndValidity();
				}

				// double check that enrollment is available after switching AY
				if (isApplying.value === true) {
					const content = formGroup.get('content').value as IApplicationContent;
					if (
						(this.academicYearApplying === content.currentAcademicYear &&
							(this.today < this.dotNetDatePipe.transform(content.currentOpenEnrollmentBeginDate) ||
								this.today > this.dotNetDatePipe.transform(content.currentLastDayOfSchool))) ||
						(this.academicYearApplying === content.nextAcademicYear &&
							this.today < this.dotNetDatePipe.transform(content.nextOpenEnrollmentBeginDate)) ||
						(this.academicYearApplying !== content.currentAcademicYear && this.academicYearApplying !== content.nextAcademicYear)
					) {
						this.removeSchoolToApply(this.nearbySchools.controls.indexOf(formGroup));
					}
				}
			});
		}
	}
}
